import { evaluateAuthority, type AuthorityRule } from "@/lovable-demo/lib/governance/authority";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import { validatePfmeaChange } from "./pfmea-governance";
import type { PfmeaAfterField, PfmeaRow, ProblemRecord } from "./types";

/**
 * Package #2.2 — dual sign-off for PFMEA after-action ratings.
 *
 * A proposal never changes the rating. It applies only once one Quality
 * approver and one Manufacturing Engineering approver — two different people,
 * neither the proposer — have both approved. Either rejection closes it.
 * The database enforces the same rules (triggers + transactional functions);
 * this module mirrors them so the UI can explain what is allowed.
 */

export type SignoffRole = "quality" | "manufacturing" | "director";
export type SignoffStep =
  "approve_quality" | "approve_manufacturing" | "approve_director" | "reject" | "applied";
export type RequestStatus = "pending" | "applied" | "rejected";

export const QUALITY_ROLES: AppRole[] = ["quality_engineer", "quality_director"];
export const MANUFACTURING_ROLES: AppRole[] = ["manufacturing_engineer"];
export const DIRECTOR_ROLES: AppRole[] = ["quality_director"];
export const PROPOSER_ROLES: AppRole[] = [
  "admin",
  "quality_director",
  "quality_engineer",
  "manufacturing_engineer",
];
export const SIGNOFF_ROLE_LABEL: Record<SignoffRole, string> = {
  quality: "Quality",
  manufacturing: "Manufacturing Engineering",
  director: "Quality Director",
};

/**
 * Package #2.3 — a Severity reduction also needs a dedicated Quality Director
 * sign-off (policy pfmea.severity_reduction_requires_director, default true).
 */
export function requiresDirector(
  req: Pick<PfmeaChangeRequest, "field" | "oldValue" | "newValue">,
  policyEnabled = true,
) {
  return policyEnabled && req.field === "sevAfter" && req.newValue < req.oldValue;
}

export function requiredRoles(
  req: Pick<PfmeaChangeRequest, "field" | "oldValue" | "newValue">,
): SignoffRole[] {
  return requiresDirector(req)
    ? ["quality", "manufacturing", "director"]
    : ["quality", "manufacturing"];
}

export const SEVERITY_REDUCTION_NOTICE =
  "Severity reduction — requires Quality, Manufacturing Engineering and Quality Director sign-off";
export const SEVERITY_DESIGN_NOTE =
  "Severity normally changes only after a design change. Reference the design change in your rationale.";

export interface PfmeaChangeRequest {
  id: string;
  recordId: string;
  rowId: string;
  field: PfmeaAfterField;
  oldValue: number;
  newValue: number;
  rpnBefore: number;
  rpnAfter: number;
  rationale: string;
  proposedByUserId: string;
  proposedByLabel: string;
  createdAt: string;
}

export interface PfmeaSignoff {
  id: string;
  requestId: string;
  step: SignoffStep;
  roleBasis: SignoffRole | "";
  decidedByUserId: string;
  decidedByLabel: string;
  comment: string;
  createdAt: string;
}

export type GovernanceResult = { ok: true; status?: string } | { ok: false; error: string };

export function signoffsFor(requestId: string, all: PfmeaSignoff[]) {
  return all.filter((s) => s.requestId === requestId);
}

export function requestStatus(signoffs: PfmeaSignoff[]): RequestStatus {
  if (signoffs.some((s) => s.step === "reject")) return "rejected";
  if (signoffs.some((s) => s.step === "applied")) return "applied";
  return "pending";
}

export function outstandingRoles(
  request: Pick<PfmeaChangeRequest, "field" | "oldValue" | "newValue">,
  signoffs: PfmeaSignoff[],
): SignoffRole[] {
  return requiredRoles(request).filter((r) => !signoffs.some((s) => s.step === `approve_${r}`));
}

export function openRequestFor(
  requests: PfmeaChangeRequest[],
  signoffs: PfmeaSignoff[],
  rowId: string,
  field: PfmeaAfterField,
) {
  return requests.find(
    (r) =>
      r.rowId === rowId &&
      r.field === field &&
      requestStatus(signoffsFor(r.id, signoffs)) === "pending",
  );
}

export function rolesFor(role: SignoffRole): AppRole[] {
  return role === "quality"
    ? QUALITY_ROLES
    : role === "manufacturing"
      ? MANUFACTURING_ROLES
      : DIRECTOR_ROLES;
}

export function holdsSignoffRole(roles: AppRole[], role: SignoffRole) {
  const list = rolesFor(role);
  return roles.some((r) => list.includes(r));
}

/**
 * Which roles this person may sign under right now, or why they cannot.
 * Separation of duties: not the proposer, and at most one sign-off per person.
 */
export function signoffEligibility(args: {
  userId: string | null;
  roles: AppRole[];
  request: PfmeaChangeRequest;
  signoffs: PfmeaSignoff[];
}): { roles: SignoffRole[]; reason: string | null } {
  const mine = signoffsFor(args.request.id, args.signoffs);
  if (requestStatus(mine) !== "pending") return { roles: [], reason: "This request is closed." };
  if (!args.userId) return { roles: [], reason: "Sign in to sign off." };
  if (args.request.proposedByUserId === args.userId)
    return { roles: [], reason: "You proposed this change, so you cannot approve or reject it." };
  if (mine.some((s) => s.decidedByUserId === args.userId && s.step !== "applied"))
    return {
      roles: [],
      reason: "You have already signed off. The other approval must come from a different person.",
    };
  const roles = outstandingRoles(args.request, mine).filter((r) => holdsSignoffRole(args.roles, r));
  if (!roles.length)
    return {
      roles: [],
      reason: "Awaiting an approver holding the outstanding role.",
    };
  return { roles, reason: null };
}

/** Requests on this record that the person could sign off now. */
export function awaitingMe(args: {
  userId: string | null;
  roles: AppRole[];
  requests: PfmeaChangeRequest[];
  signoffs: PfmeaSignoff[];
}) {
  return args.requests.filter(
    (request) => signoffEligibility({ ...args, request }).roles.length > 0,
  );
}

export function validateSignoffComment(comment: string): string | null {
  if (!comment.trim()) return "A comment is required.";
  if (comment.trim().length < 5) return "The comment must be at least 5 characters.";
  return null;
}

/** Minimal RPC surface so the flow can be tested without a database. */
export type Rpc = (
  fn: string,
  args: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

/** Class A — propose. Never changes the rating; creates a pending request. */
export async function submitPfmeaProposal(
  rpc: Rpc,
  ctx: {
    record: ProblemRecord;
    row: PfmeaRow | undefined;
    field: PfmeaAfterField;
    newValue: number;
    rationale: string;
    roles: AppRole[];
    rules: AuthorityRule[];
    openRequest: PfmeaChangeRequest | undefined;
  },
): Promise<GovernanceResult> {
  const invalid = validatePfmeaChange(ctx.row, ctx.field, ctx.newValue, ctx.rationale);
  if (invalid || !ctx.row) return { ok: false, error: invalid ?? "PFMEA row not found." };
  if (ctx.openRequest)
    return {
      ok: false,
      error:
        "A change to this rating is already pending sign-off. It must be approved or rejected first.",
    };
  const verdict = evaluateAuthority({
    decisionType: "pfmea.propose",
    record: ctx.record,
    roles: ctx.roles,
    rules: ctx.rules,
    exactRoles: PROPOSER_ROLES,
  });
  if (!verdict.authorised)
    return { ok: false, error: `Not authorised to propose PFMEA changes. ${verdict.reason}` };
  const { error } = await rpc("pfmea_propose_change", {
    p_record_id: ctx.record.id,
    p_row_id: ctx.row.id,
    p_field: ctx.field,
    p_new_value: ctx.newValue,
    p_rationale: ctx.rationale.trim(),
    p_authority_rule_key: verdict.matchedRuleKey ?? "pfmea.propose",
  });
  if (error) return { ok: false, error: `Nothing was recorded. ${error.message}` };
  return { ok: true, status: "pending" };
}

/** Class A — approve or reject under one role. Applies when both approvals exist. */
export async function submitPfmeaSignoff(
  rpc: Rpc,
  ctx: {
    record: ProblemRecord;
    request: PfmeaChangeRequest;
    signoffs: PfmeaSignoff[];
    userId: string | null;
    roles: AppRole[];
    rules: AuthorityRule[];
    decision: "approve" | "reject";
    role: SignoffRole;
    comment: string;
  },
): Promise<GovernanceResult> {
  const badComment = validateSignoffComment(ctx.comment);
  if (badComment) return { ok: false, error: badComment };
  const eligible = signoffEligibility(ctx);
  if (!eligible.roles.includes(ctx.role))
    return { ok: false, error: eligible.reason ?? "You cannot sign off under this role." };
  const decisionType =
    ctx.role === "quality"
      ? "pfmea.approve_quality"
      : ctx.role === "manufacturing"
        ? "pfmea.approve_manufacturing"
        : "pfmea.approve_severity_reduction";
  const verdict = evaluateAuthority({
    decisionType,
    record: ctx.record,
    roles: ctx.roles,
    rules: ctx.rules,
    exactRoles: rolesFor(ctx.role),
  });
  if (!verdict.authorised)
    return { ok: false, error: `Not authorised for ${SIGNOFF_ROLE_LABEL[ctx.role]} sign-off.` };
  const { data, error } = await rpc("pfmea_sign_off", {
    p_request_id: ctx.request.id,
    p_decision: ctx.decision,
    p_role: ctx.role,
    p_comment: ctx.comment.trim(),
    p_authority_rule_key: verdict.matchedRuleKey ?? decisionType,
  });
  if (error) return { ok: false, error: `Nothing was recorded. ${error.message}` };
  return { ok: true, status: String(data ?? "") };
}

// ---------------------------------------------------------------- row mapping

export function requestFromRow(r: {
  id: string;
  record_id: string;
  row_id: string;
  field: string;
  old_value: number;
  new_value: number;
  rpn_before: number;
  rpn_after: number;
  rationale: string;
  proposed_by_user_id: string;
  proposed_by_label: string;
  created_at: string;
}): PfmeaChangeRequest {
  return {
    id: r.id,
    recordId: r.record_id,
    rowId: r.row_id,
    field: r.field as PfmeaAfterField,
    oldValue: r.old_value,
    newValue: r.new_value,
    rpnBefore: r.rpn_before,
    rpnAfter: r.rpn_after,
    rationale: r.rationale,
    proposedByUserId: r.proposed_by_user_id,
    proposedByLabel: r.proposed_by_label,
    createdAt: r.created_at,
  };
}

export function signoffFromRow(r: {
  id: string;
  request_id: string;
  step: string;
  role_basis: string;
  decided_by_user_id: string;
  decided_by_label: string;
  comment: string;
  created_at: string;
}): PfmeaSignoff {
  return {
    id: r.id,
    requestId: r.request_id,
    step: r.step as SignoffStep,
    roleBasis: r.role_basis as SignoffRole | "",
    decidedByUserId: r.decided_by_user_id,
    decidedByLabel: r.decided_by_label,
    comment: r.comment,
    createdAt: r.created_at,
  };
}
